import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { catalogApi } from '../api/catalogApi';
import { progressApi } from '../api/progressApi';
import { useApi, useAsync } from '../hooks/useApi';
import { useSpeech } from '../hooks/useSpeech';
import { useSettings } from '../settings/SettingsContext';
import { Loading, LoadError } from '../components/Loading';
import Crumbs from '../components/Crumbs';
import ProgressBar from '../components/ProgressBar';
import StudyBlocks from '../components/StudyBlocks';
import FlashcardConfigPanel from '../study/FlashcardConfigPanel';
import { describeSetup } from '../study/describeSetup';
import { validateFieldModes } from '../../../shared/api';

type Tab = 'study' | 'exercises';

export default function SectionPage() {
  const { sectionId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const api = useApi();
  const { settings, update } = useSettings();

  const tab: Tab = params.get('tab') === 'exercises' ? 'exercises' : 'study';
  const setTab = (next: Tab) => setParams(next === 'study' ? {} : { tab: next }, { replace: true });

  const section = useAsync(() => catalogApi.section(sectionId, api), [sectionId, api]);
  const language = useAsync(
    () => (section.data ? catalogApi.language(section.data.languageCode, api) : Promise.resolve(null)),
    [section.data?.languageCode, api],
  );
  const progress = useAsync(
    () => (section.data ? progressApi.book(section.data.bookId, api) : Promise.resolve(null)),
    [section.data?.bookId, api],
  );

  const [markingRead, setMarkingRead] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);

  const speech = useSpeech(language.data?.speechTag ?? '');

  const state = useMemo(
    () => progress.data?.sections.find((s) => s.sectionId === sectionId) ?? null,
    [progress.data, sectionId],
  );

  if (section.loading || language.loading) {
    return (
      <main className="page">
        <Loading what="this section" />
      </main>
    );
  }
  if (section.error || !section.data || !language.data) {
    return (
      <main className="page">
        <LoadError message={section.error ?? 'Section not found'} onRetry={section.reload} />
      </main>
    );
  }

  const data = section.data;
  const lang = language.data;
  const audioUnavailable = !speech.supported || !speech.voiceAvailable;
  const configProblem = validateFieldModes(settings.flashcards.fields, lang);

  const index = data.siblings.findIndex((s) => s.id === data.id);
  const previous = index > 0 ? data.siblings[index - 1] : null;
  const next = index >= 0 && index < data.siblings.length - 1 ? data.siblings[index + 1] : null;

  const markRead = async () => {
    setMarkingRead(true);
    try {
      await progressApi.markStudied(data.id, api);
      progress.reload();
    } finally {
      setMarkingRead(false);
    }
  };

  return (
    <main className="page">
      <Crumbs
        items={[
          { label: 'Languages', to: '/' },
          { label: lang.name, to: `/languages/${lang.code}` },
          { label: data.bookTitle, to: `/books/${data.bookId}` },
          { label: `Chapter ${data.chapterNumber}`, to: `/chapters/${data.chapterId}` },
          { label: data.number },
        ]}
      />

      <header className="page-header">
        <p className="page-eyebrow">
          {data.number} · {data.chapterTitle}
        </p>
        <h1>{data.title}</h1>
        <p className="page-description">{data.summary}</p>
        {state && state.cardsSeen > 0 && (
          <div className="section-progress">
            <ProgressBar
              value={state.cardCount > 0 ? (state.cardsKnown / state.cardCount) * 100 : 0}
              label={`${state.cardsKnown} of ${state.cardCount} known`}
            />
          </div>
        )}
      </header>

      <div className="tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'study'}
          className={`tab${tab === 'study' ? ' tab--active' : ''}`}
          onClick={() => setTab('study')}
        >
          Study
          <span className="tab-meta">{data.studyMinutes} min</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'exercises'}
          className={`tab${tab === 'exercises' ? ' tab--active' : ''}`}
          onClick={() => setTab('exercises')}
        >
          Exercises
          <span className="tab-meta">{data.cardCount} cards</span>
        </button>
      </div>

      {tab === 'study' ? (
        <>
          {!speech.supported && (
            <p className="callout callout--quiet">
              This browser has no speech synthesis, so nothing on this page can be played aloud.
            </p>
          )}
          {speech.supported && !speech.voiceAvailable && (
            <p className="callout callout--quiet">
              No {lang.name} voice is installed on this device, so audio is turned off. Adding one
              through your operating system's language settings will enable it here.
            </p>
          )}

          <StudyBlocks
            blocks={data.study}
            cards={data.cards}
            language={lang}
            speech={speech}
            speechRate={settings.flashcards.speechRate}
          />

          <div className="study-footer">
            {state?.studied ? (
              <p className="muted">You have marked this section as read.</p>
            ) : (
              <button type="button" className="btn" onClick={markRead} disabled={markingRead}>
                {markingRead ? 'Saving…' : 'Mark as read'}
              </button>
            )}
            <button type="button" className="btn btn--primary" onClick={() => setTab('exercises')}>
              Practice these {data.cardCount} cards →
            </button>
          </div>
        </>
      ) : (
        <>
          <section className="exercise-card">
            <h2 className="exercise-title">Flashcards</h2>
            <p className="muted">
              {data.cardCount} cards from this section
              {state && state.cardsKnown > 0 && `, ${state.cardsKnown} of them already known`}.
            </p>

            <div className="setup-summary">
              <div>
                <span className="setup-label">Card setup</span>
                <p className="setup-value">{describeSetup(settings.flashcards, lang)}</p>
              </div>
              <button type="button" className="btn btn--small" onClick={() => setConfigOpen((o) => !o)}>
                {configOpen ? 'Done' : 'Change'}
              </button>
            </div>

            {configOpen && (
              <FlashcardConfigPanel
                config={settings.flashcards}
                language={lang}
                audioUnavailable={audioUnavailable}
                voiceName={speech.voiceName}
                onChange={(flashcards) => update({ flashcards })}
                sessionSize={settings.sessionSize}
                onSessionSizeChange={(sessionSize) => update({ sessionSize })}
                deckSize={data.cardCount}
              />
            )}

            {configProblem && <p className="error">{configProblem}</p>}

            <button
              type="button"
              className="btn btn--primary btn--large"
              disabled={Boolean(configProblem)}
              onClick={() => navigate(`/sections/${data.id}/flashcards`)}
            >
              Start
            </button>
          </section>

          <p className="muted footnote">
            More exercise types — the book's own fill-in-the-blank drills — are not built yet.
            Flashcards are the whole exercise tab for now.
          </p>
        </>
      )}

      <nav className="section-nav">
        {previous ? (
          <Link to={`/sections/${previous.id}`} className="section-nav-link">
            <span className="section-nav-dir">← Previous</span>
            <span className="section-nav-title">
              {previous.number} {previous.title}
            </span>
          </Link>
        ) : (
          <span />
        )}
        {next ? (
          <Link to={`/sections/${next.id}`} className="section-nav-link section-nav-link--next">
            <span className="section-nav-dir">Next →</span>
            <span className="section-nav-title">
              {next.number} {next.title}
            </span>
          </Link>
        ) : (
          <Link to={`/chapters/${data.chapterId}`} className="section-nav-link section-nav-link--next">
            <span className="section-nav-dir">Finish →</span>
            <span className="section-nav-title">Back to chapter {data.chapterNumber}</span>
          </Link>
        )}
      </nav>
    </main>
  );
}
